import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Err } from 'ts-results'

import { useNotifications } from 'app/application/application'
import { DataError } from 'app/common/application'
import { PlusIcon } from 'app/common/presentation/icons'
import { getInitials } from 'app/common/presentation/initials'
import { useUser, useWorkspace } from 'app/user/application'
import { WorkspaceUser } from 'app/user/types'

import { Button, Chip, Table } from 'components'

import { PageHeader } from '../components'
import InviteModal from './InviteModal'
import styles from './Members.module.scss'

const formatJoinedDate = (value: string) =>
  new Intl.DateTimeFormat('en', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value))

const Members = () => {
  const { t } = useTranslation('settings')
  const { createNotification, te } = useNotifications()
  const { workspace } = useUser()
  const { getWorkspaceUsers, inviteUser } = useWorkspace()

  const [users, setUsers] = useState<WorkspaceUser[]>([])
  const [inviteUserModalOpen, setInviteUserModalOpen] = useState(false)
  const [inviteUserError, setInviteUserError] = useState<string>()

  const loadWorkspaceUsers = useCallback(
    () =>
      getWorkspaceUsers().then((result) => {
        if (result.ok) {
          setUsers(result.val)
        } else {
          createNotification({
            message: result.val.message,
            title: te(`http.${result.val.code}`),
            type: 'error',
          })
        }
      }),
    [createNotification, getWorkspaceUsers, t],
  )

  useEffect(() => void loadWorkspaceUsers(), [loadWorkspaceUsers])
  useEffect(() => setInviteUserError(undefined), [inviteUserModalOpen])

  const handleInviteUser = (emails: string) => {
    setInviteUserError(undefined)

    const addresses = emails
      .split(',')
      .map((email) => email.trim())
      .filter(Boolean)

    void Promise.allSettled(addresses.map((email) => inviteUser(email))).then((results) => {
      void loadWorkspaceUsers()

      const errorResult = results.find(
        (result): result is PromiseFulfilledResult<Err<DataError<number>>> =>
          result.status === 'fulfilled' && !result.value.ok,
      )?.value.val
      if (errorResult) {
        setInviteUserError(errorResult.message)
      } else {
        setInviteUserModalOpen(false)
      }
    })
  }

  return (
    <div className={styles.users}>
      <PageHeader
        action={
          <Button onClick={() => setInviteUserModalOpen(true)} rightIcon={<PlusIcon />}>
            {t('users.invite.open')}
          </Button>
        }
        description={t('pages.members.description', { workspace: workspace?.name ?? '' })}
        title={t('pages.members.title')}
      />

      <div className={styles.tableCard}>
        <Table.Table
          className={styles.table}
          columns={[
            t('users.columns.name'),
            t('users.columns.email'),
            t('users.columns.role'),
            t('users.columns.joined'),
          ]}
          data={users}
          renderRow={(user) => {
            const isPending = user.status === 'invited'
            const role = user.isAdmin ? 'admin' : 'member'

            return (
              <Table.Row key={user.userId}>
                <Table.Cell className={styles.nameCell}>
                  <span className={styles.avatar}>{getInitials(user)}</span>
                  <span>
                    {user.firstName} {user.lastName}
                  </span>
                </Table.Cell>
                <Table.Cell>{user.email}</Table.Cell>
                <Table.Cell>
                  <Chip color={isPending ? 'neutral' : role === 'admin' ? 'purple' : 'blue'}>
                    {t(`users.role.${isPending ? 'pending' : role}`)}
                  </Chip>
                </Table.Cell>
                <Table.Cell>{user.joined ? formatJoinedDate(user.joined) : '-'}</Table.Cell>
              </Table.Row>
            )
          }}
        />
      </div>

      <InviteModal
        inviteUser={handleInviteUser}
        inviteUserError={inviteUserError}
        onClose={() => setInviteUserModalOpen(false)}
        open={inviteUserModalOpen}
      />
    </div>
  )
}

export default Members
